import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CONFIG, BOOKING_STATUS } from '../config'

export default function BookingStatus() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [bookingId, setBookingId] = useState('')
  const [booking, setBooking] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleInputChange = (e) => {
    setBookingId(e.target.value)
  }

  const handleStatusCheck = async (e) => {
    e.preventDefault()
    if (!bookingId) {
      setError(t('invalid_booking_id'))
      return
    }

    setLoading(true)
    setError('')
    setBooking(null)

    try {
      const response = await fetch(`${CONFIG.API_URL}/bookings/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ booking_id: bookingId })
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || t('booking_not_found'))
        return
      }

      setBooking(data.booking)

    } catch (error) {
      setError(t('network_error'))
    } finally {
      setLoading(false)
    }
  }

  const getStatusClass = (status) => {
    switch (status) {
      case BOOKING_STATUS.APPROVED:
        return 'status-approved'
      case BOOKING_STATUS.REJECTED:
        return 'status-rejected'
      case BOOKING_STATUS.PENDING:
        return 'status-pending'
      default:
        return ''
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case BOOKING_STATUS.APPROVED:
        return t('approved')
      case BOOKING_STATUS.REJECTED:
        return t('rejected')
      case BOOKING_STATUS.PENDING:
        return t('pending')
      default:
        return status
    }
  }

  const getStatusMessage = (booking) => {
    if (booking.status === BOOKING_STATUS.APPROVED) {
      return {
        type: 'success',
        message: t('approved_by_both_admins')
      }
    } else if (booking.status === BOOKING_STATUS.REJECTED) {
      return {
        type: 'error',
        message: t('booking_rejected')
      }
    } else if (booking.status === BOOKING_STATUS.PENDING) {
      const approvals = [booking.admin1_approved, booking.admin2_approved].filter(Boolean).length
      if (approvals === 1) {
        return {
          type: 'warning',
          message: t('approved_by_one_admin')
        }
      }
      return {
        type: 'warning',
        message: t('booking_pending')
      }
    }
    return { type: 'info', message: 'Status tidak diketahui' }
  }

  return (
    <div className="card">
      <h2 className="section-title">{t('booking_status_title')}</h2>
      <p className="text-secondary mb-lg">
        {t('booking_status_description')}
      </p>

      <form onSubmit={handleStatusCheck}>
        <div className="form-group">
          <label htmlFor="booking_id">{t('booking_id')}</label>
          <input
            type="text"
            id="booking_id"
            value={bookingId}
            onChange={handleInputChange}
            placeholder={t('booking_id')}
            required
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? t('checking_status') : t('check_status')}
        </button>
      </form>

      {error && (
        <div className="alert alert-error mt-md">{error}</div>
      )}

      {booking && (
        <div className="mt-xl">
          {(() => {
            const statusInfo = getStatusMessage(booking)
            return (
              <div className={`alert alert-${statusInfo.type}`}>
                {statusInfo.message}
              </div>
            )
          })()}

          <div className="booking-card">
            <div className="booking-header">
              <div className="booking-info">
                <h3>{booking.room}</h3>
                <p><strong>{t('date_time')}:</strong> {booking.date} {booking.start_time} - {booking.end_time}</p>
                <p><strong>{t('requester')}:</strong> {booking.user}</p>
                <p><strong>{t('event')}:</strong> {booking.event_description}</p>
              </div>
              <span className={`status-badge ${getStatusClass(booking.status)}`}>
                {getStatusLabel(booking.status)}
              </span>
            </div>

            <div className="booking-approval-status mt-md p-md bg-gray-light rounded border">
              <h4 className="mb-sm font-bold text-gray-dark">{t('admin_approval_status')}:</h4>
              <p className="text-secondary">
                {t('admin_1')}: {booking.admin1_approved ? `✓ ${t('approved')}` : `⏳ ${t('waiting')}`}
              </p>
              <p className="text-secondary">
                {t('admin_2')}: {booking.admin2_approved ? `✓ ${t('approved')}` : `⏳ ${t('waiting')}`}
              </p>
            </div>

            {[BOOKING_STATUS.APPROVED, BOOKING_STATUS.REJECTED].includes(booking.status) && (
              <div className="mt-md">
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/edit/${booking.id}?token=${booking.token}`)}
                >
                  {t('edit_booking')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
